// @ts-nocheck

import React, { useState, type Node, useCallback, useRef, useEffect, forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { useViewportContext } from '../../contexts/ViewportContext';
import { getAppDOMElement } from '../../DOMElements';
import theme from '../../styled-components/dark';

const Wrapper = styled.div`
  overflow: hidden;
  z-index: ${theme.ZIndices.matchBetFilters};

  transition: none 300ms ease-in-out;
  transition-property: transform, background;
  font-size: 1em;
  padding: 0.6em;

  ${({ $offsetTop, $width, $height, $hide, $isMobile, $isFixed, $hasAutoStickyBehaviour }) =>
    $hasAutoStickyBehaviour
      ? css`
          position: ${$isMobile || !$isFixed ? 'sticky' : 'fixed'};
          top: ${$offsetTop ? `${$offsetTop}px` : '0px'};
          transform: translateY(${$hide ? -$height : 0}px);
          background: var(--match-bet-filters-main-background, ${$isMobile || $isFixed ? '#000' : 'none'});

          ${!$isMobile && $isFixed
            ? css`
                width: ${$width}px;
              `
            : null};
        `
      : undefined}
`;

const Placeholder = styled.div`
  height: ${({ $height }) => $height}px;
`;

type StickyScrollWrapperProps = {
  className?: string;
  children: Node;
  fixedOffset?: number;
  contentOffset?: number;
  hasAutoStickyBehaviour?: boolean;
  'data-testid'?: string;
};

const StickyScrollWrapper = (
  {
    className,
    children,
    fixedOffset = 0,
    contentOffset = 0,
    hasAutoStickyBehaviour = true,
    'data-testid': testId = '',
  },
  forwardedRef,
) => {
  const { navbarHeight, safeAreaInset } = useViewportContext();
  const scrollElement = Target.__MOBILE__ ? getAppDOMElement() : window;

  const initialWrapperPosition = useRef(0);
  const scrollPosition = useRef(0);
  const wrapPosition = useRef(0);

  const [wrapperSize, setWrapperSize] = useState({ width: 0, height: 0 });
  const [hideWrapper, setHideWrapper] = useState(false);
  const [isFixed, setIsFixed] = useState(false);

  const measuredWrapperHeight = useCallback(
    (node) => {
      if (node) {
        const { bottom, height, width } = node.getBoundingClientRect();
        const scrollY = scrollElement.scrollTop ?? scrollElement.scrollY;

        initialWrapperPosition.current = bottom - navbarHeight + scrollY;
        setWrapperSize({ width, height });

        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      }
    },
    [scrollElement, navbarHeight, forwardedRef],
  );

  useEffect(() => {
    if (hasAutoStickyBehaviour) {
      const onScroll = (e) => {
        const elementScrollTop = e.target.scrollTop ?? e.target.documentElement.scrollTop;
        const newScrollPosition = elementScrollTop + fixedOffset + safeAreaInset.top;
        const wrapperPosition = initialWrapperPosition.current + contentOffset;

        const hasScrollUp = newScrollPosition < scrollPosition.current;
        const isSticky = newScrollPosition > wrapperPosition;
        const shouldShowWrapper = !hideWrapper && newScrollPosition === scrollPosition.current;

        setIsFixed(isSticky);
        setHideWrapper(isSticky && !(hasScrollUp || shouldShowWrapper));

        scrollPosition.current = newScrollPosition;
        wrapPosition.current = wrapperPosition;
      };

      scrollElement?.addEventListener('scroll', onScroll);
      return () => {
        scrollElement?.removeEventListener('scroll', onScroll);
      };
    }
  }, [wrapperSize, scrollElement, fixedOffset, safeAreaInset, contentOffset, hideWrapper, hasAutoStickyBehaviour]);

  return (
    <>
      {!Target.__MOBILE__ && isFixed ? <Placeholder $height={wrapperSize.height} /> : null}
      <Wrapper
        ref={measuredWrapperHeight}
        className={className}
        $offsetTop={fixedOffset}
        $width={wrapperSize.width}
        $height={wrapperSize.height}
        $hide={hideWrapper}
        $isMobile={Target.__MOBILE__}
        $isFixed={isFixed}
        $hasAutoStickyBehaviour={hasAutoStickyBehaviour}
        data-testid={testId}
      >
        {children}
      </Wrapper>
    </>
  );
};

export default forwardRef<StickyScrollWrapperProps, Wrapper>(StickyScrollWrapper);
